// When a P&L category is renamed, its typed values move with it.
//
// Run: deno test --allow-read src/features/pnlAnalytics/categoryRenameValues.test.ts
// Run: npm run verify:pnl-category-rename-values
//
// WHY THIS EXISTS, AND WHY IT IS NOT PART OF THE RENAME ALIAS WRITE.
// claims_pnl_monthly_values stores the category as a STRING, so renaming a
// category in the editor leaves every existing row under the old string.
// aliasWrite.ts already makes the ANALYTICS side survive that, by pointing
// the new string at the old string's metric key and leaving the old alias in
// place, but it does nothing for the two surfaces that read the
// configuration directly: the project P&L tab renders one row per configured
// category, so the old rows vanish from it, and the company dashboard sums
// every manual row, so they keep counting. A rename therefore produced
// exactly the orphan state that the removal guard
// (categoryRemoval.ts) exists to prevent, by a different door.
//
// With removal now refused while values exist, renaming was the only
// remaining way to create an orphan through the editor. Carrying the values
// closes it, and is the answer that keeps renaming working: refusing a
// rename would have been the cheaper fix and would have taken away a feature
// SEQ-180 deliberately built.
//
// WHY THIS IS SAFE TO RUN AS A DATA WRITE. It only ever runs on renames that
// detectCategoryRenames has already vouched for, and that function is
// deliberately paranoid: it returns nothing at all unless the list length is
// unchanged and every other field on the entry is identical, precisely so an
// add or a delete can never be mistaken for a rename. It relabels rows; it
// creates nothing and deletes nothing, so the worst case of a wrong call is
// a second rename to put the label back.
//
// The alias write still happens alongside it and is not redundant: a row
// this update misses (a project the caller cannot see under RLS, or a
// partial failure) still resolves correctly on the analytics side.

import type { CategoryRename } from "./renameDetection.ts";

/*
 * The client is described structurally rather than imported as
 * SupabaseClient, for the same reason renameDetection.ts declares its own
 * CategoryLike: Deno cannot resolve @supabase/supabase-js's types without a
 * populated node_modules, so importing it would make this module untestable
 * by the only test runner this repo has. It is also what lets the test drive
 * the real function against a recording fake instead of asserting on a
 * message string and hoping the call underneath matches it.
 */
export interface CategoryValueUpdateResult {
  data: { id: string }[] | null;
  error: { message: string } | null;
}

export interface CategoryValueFilter {
  eq(column: string, value: string): CategoryValueFilter;
  select(columns: string): PromiseLike<CategoryValueUpdateResult>;
}

export interface CategoryValueTable {
  update(values: { category: string }): CategoryValueFilter;
}

export interface CategoryValueClient {
  from(table: string): CategoryValueTable;
}

export interface CarryRenamedCategoryValuesResult {
  /** Null on full success. Non-null names the first failure; earlier renames in the batch may already have been carried. */
  error: string | null;
  /** Rows relabelled, across every rename in the batch. */
  movedRowCount: number;
}

/**
 * Relabels claims_pnl_monthly_values.category from each rename's old string
 * to its new one, for this organisation only.
 *
 * Follows the codebase's X.1 rule: every Supabase call's `{ error }` is
 * checked explicitly. The returned rows are counted rather than assumed,
 * because an UPDATE refused by row level security returns neither an error
 * nor any rows, which is the silent no-op this codebase has been bitten by.
 */
export async function carryRenamedCategoryValues(
  supabase: CategoryValueClient,
  organizationId: string,
  renames: CategoryRename[],
): Promise<CarryRenamedCategoryValuesResult> {
  let movedRowCount = 0;

  for (const { oldName, newName } of renames) {
    const { data, error } = await supabase
      .from("claims_pnl_monthly_values")
      .update({ category: newName })
      .eq("organization_id", organizationId)
      .eq("category", oldName)
      .select("id");

    if (error) {
      return {
        error: `Could not move the values typed under "${oldName}" to "${newName}": ${error.message}`,
        movedRowCount,
      };
    }

    movedRowCount += data?.length ?? 0;
  }

  return { error: null, movedRowCount };
}

/**
 * What to tell the user when the relabel fails partway.
 *
 * Named here rather than written inline at the call site so the wording is
 * testable, and so it says the one thing that matters: the configuration
 * moved and some data did not, which is the orphan state, and where to see
 * it.
 */
export function renameCarryFailureMessage(error: string): string {
  return `${error} The category list was saved, so any values left under the old name now have no row on the P&L. They are listed under "Admin: unmapped data" on the P&L analytics page.`;
}
